import * as Sentry from "@sentry/react";

/**
 * Front-end instrumentation for the supervisor SSO round trip.
 *
 * The login page can only start the code exchange when the IdP hands back
 * `?code=...` on the login route (see login.tsx). When the callback arrives in
 * any other shape the page silently re-renders the email form, which is
 * indistinguishable — to the person using it — from "nothing happened". This
 * module records what we asked the IdP for, what actually came back, and
 * reports the mismatch to Sentry, so diagnosing a failed SSO round trip no
 * longer depends on the customer opening DevTools.
 *
 * Nothing here records a credential: authorization codes, tokens and `state`
 * are logged as "present (N chars)" and never by value. OAuth `error` and
 * `error_description` are not secret and are captured verbatim — they carry the
 * AADSTS code that names the actual fault.
 */

const ATTEMPT_KEY = "sso-attempt";

/** A return leg lands within seconds; older markers are abandoned attempts. */
const ATTEMPT_TTL_MS = 15 * 60 * 1000;

/** Never log the value of these — presence and length only. */
const SECRET_PARAMS = [
	"code",
	"id_token",
	"access_token",
	"refresh_token",
	"token",
	"state",
	"session_state",
	"client_info",
	"code_verifier",
];

export type SsoFailure =
	/** IdP refused and named a reason (AADSTS…) that we currently discard. */
	| "idp-error"
	/** Code came back in the URL fragment; login.tsx only reads the query. */
	| "code-in-fragment"
	/** Code reached the browser but was stripped before login.tsx mounted. */
	| "code-dropped-before-login"
	/** Came back from the IdP carrying nothing — form_post, or a proxy stripped it. */
	| "empty-callback"
	/** No evidence the browser ever returned from the IdP. */
	| "no-callback";

export interface SsoDiagnostic {
	reference: string;
	classification: SsoFailure;
	/** Whether the user should be told; an abandoned attempt is not an error. */
	isUserVisible: boolean;
	details: string;
}

interface SsoAttempt {
	startedAt: number;
	startedFrom: string;
	authorizeOrigin: string;
	responseType: string | null;
	responseMode: string | null;
	redirectUri: string | null;
	clientId: string | null;
	scope: string | null;
	prompt: string | null;
	hasLoginHint: boolean;
	hasDomainHint: boolean;
	hasState: boolean;
}

/**
 * The URL as it was when the bundle loaded. Captured at module scope — before
 * React renders — because a callback that lands on a route other than /login is
 * bounced by PrivateRoute, and react-router's <Navigate to='/login'> drops the
 * query string on the way. By the time any component mounts, the evidence is
 * already gone from window.location.
 */
const LANDING = {
	pathname: window.location.pathname,
	search: window.location.search,
	hash: window.location.hash,
	referrer: document.referrer,
};

/** Enterprise browser policies can block storage; never fail the login for it. */
const safeRead = (key: string): string | null => {
	try {
		return localStorage.getItem(key);
	} catch (err) {
		return null;
	}
};

const safeWrite = (key: string, value: string): void => {
	try {
		localStorage.setItem(key, value);
	} catch (err) {
		// Diagnostics are best-effort — a blocked store must not break sign-in.
	}
};

const safeRemove = (key: string): void => {
	try {
		localStorage.removeItem(key);
	} catch (err) {
		// See safeWrite.
	}
};

const describeParams = (params: URLSearchParams): Record<string, string> => {
	const described: Record<string, string> = {};
	params.forEach((value, key) => {
		described[key] = SECRET_PARAMS.includes(key.toLowerCase())
			? `present (${value.length} chars)`
			: value;
	});
	return described;
};

const makeReference = (): string =>
	Math.floor(Math.random() * 0xffff)
		.toString(16)
		.toUpperCase()
		.padStart(4, "0");

const readAttempt = (): SsoAttempt | null => {
	const raw = safeRead(ATTEMPT_KEY);
	if (!raw) return null;
	try {
		return JSON.parse(raw) as SsoAttempt;
	} catch (err) {
		safeRemove(ATTEMPT_KEY);
		return null;
	}
};

/**
 * Called immediately before handing the browser to the IdP. Stores the
 * non-secret shape of the authorize request, which is what decides the shape of
 * the callback and therefore the first thing to read when no code comes back.
 */
export const recordSsoAttempt = (ssoLoginUrl: string): void => {
	try {
		const authorizeUrl = new URL(ssoLoginUrl);
		const params = authorizeUrl.searchParams;
		const attempt: SsoAttempt = {
			startedAt: Date.now(),
			startedFrom: `${window.location.origin}${window.location.pathname}`,
			authorizeOrigin: authorizeUrl.origin,
			responseType: params.get("response_type"),
			responseMode: params.get("response_mode"),
			redirectUri: params.get("redirect_uri"),
			clientId: params.get("client_id"),
			scope: params.get("scope"),
			prompt: params.get("prompt"),
			// login_hint carries the user's email — record presence, not value.
			hasLoginHint: params.has("login_hint"),
			hasDomainHint: params.has("domain_hint"),
			hasState: params.has("state"),
		};
		safeWrite(ATTEMPT_KEY, JSON.stringify(attempt));
	} catch (err) {
		// An ssoLoginUrl that isn't a URL is itself the bug: window.location
		// .replace() would resolve it as a relative path and land on the 404 page.
		Sentry.withScope((scope) => {
			scope.setLevel(Sentry.Severity.Error);
			scope.setTag("sso.classification", "unusable-login-url");
			scope.setExtra("ssoLoginUrlType", typeof ssoLoginUrl);
			Sentry.captureMessage("SSO: /Users/verify returned an unusable ssoLoginUrl");
		});
	}
};

export const clearSsoAttempt = (): void => safeRemove(ATTEMPT_KEY);

let evaluated = false;
let evaluation: SsoDiagnostic | null = null;

/**
 * Decides whether this page load is a failed SSO return leg and, if so, reports
 * it. Memoised: the marker is consumed on first call so a single attempt is
 * never reported twice (StrictMode double-invokes effects in development).
 */
export const evaluateSsoCallback = (): SsoDiagnostic | null => {
	if (evaluated) return evaluation;
	evaluated = true;

	const attempt = readAttempt();
	if (!attempt) return null;

	const elapsedMs = Date.now() - attempt.startedAt;
	if (elapsedMs > ATTEMPT_TTL_MS) {
		clearSsoAttempt();
		return null;
	}

	const landingSearch = new URLSearchParams(LANDING.search);
	const landingHash = new URLSearchParams(LANDING.hash.replace(/^#/, ""));
	const currentSearch = new URLSearchParams(window.location.search);

	// The happy path: login.tsx can see the code and will exchange it. Any
	// failure from here on already surfaces through authenticateSSO's error box.
	if (currentSearch.has("code")) {
		clearSsoAttempt();
		return null;
	}

	const error = landingSearch.get("error") ?? landingHash.get("error");
	const errorDescription =
		landingSearch.get("error_description") ??
		landingHash.get("error_description");

	let classification: SsoFailure;
	if (error) {
		classification = "idp-error";
	} else if (landingHash.has("code")) {
		classification = "code-in-fragment";
	} else if (landingSearch.has("code")) {
		classification = "code-dropped-before-login";
	} else if (
		LANDING.referrer &&
		LANDING.referrer.startsWith(attempt.authorizeOrigin)
	) {
		classification = "empty-callback";
	} else {
		classification = "no-callback";
	}

	const reference = makeReference();

	Sentry.withScope((scope) => {
		scope.setLevel(Sentry.Severity.Error);
		scope.setTag("sso.classification", classification);
		scope.setTag("sso.reference", reference);
		scope.setTag("sso.response_mode", attempt.responseMode ?? "unset");
		scope.setTag("sso.response_type", attempt.responseType ?? "unset");
		scope.setTag("sso.error", error ?? "none");
		// Lets support tie a ticket to an event without asking for the reference.
		const email = safeRead("login_username");
		if (email) scope.setUser({ email });
		scope.setExtras({
			authorizeRequest: attempt,
			callback: {
				landingPathname: LANDING.pathname,
				landingQuery: describeParams(landingSearch),
				landingFragment: describeParams(landingHash),
				currentPathname: window.location.pathname,
				currentQuery: describeParams(currentSearch),
				referrer: LANDING.referrer || "(none)",
				error,
				errorDescription,
			},
			client: {
				appUrl: process.env.REACT_APP_URL,
				elapsedMs,
				userAgent: navigator.userAgent,
			},
		});
		Sentry.captureMessage(`SSO callback failed: ${classification}`);
	});

	clearSsoAttempt();

	evaluation = {
		reference,
		classification,
		// An attempt with no sign of a return leg is usually someone closing the
		// IdP page. Report it, but do not accuse the browser of an error.
		isUserVisible: classification !== "no-callback",
		details: JSON.stringify(
			{
				reference,
				classification,
				error,
				errorDescription,
				responseMode: attempt.responseMode,
				responseType: attempt.responseType,
				redirectUri: attempt.redirectUri,
				landingPathname: LANDING.pathname,
				landingQuery: describeParams(landingSearch),
				landingFragment: describeParams(landingHash),
				referrer: LANDING.referrer || "(none)",
				userAgent: navigator.userAgent,
			},
			null,
			2
		),
	};

	return evaluation;
};
